(() => {
  const initContactSection = (root) => {
    if (!root || root.dataset.pflContactReady === 'true') return;
    root.dataset.pflContactReady = 'true';

    root.querySelectorAll('[data-pfl-contact-form]').forEach((form) => {
      const status = form.querySelector('[data-form-status]');
      const submitButton = form.querySelector('[data-submit]');
      const message = form.querySelector('[data-message]');
      const counter = form.querySelector('[data-message-count]');
      const consent = form.querySelector('[data-consent]');
      const attachment = form.querySelector('[data-attachment]');
      const attachmentError = form.querySelector('[data-attachment-error]');
      const shortcuts = [...form.querySelectorAll('[data-topic-value]')];
      const topic = form.querySelector('[data-topic-select]');

      const fieldConfig = [...form.querySelectorAll('[data-validate]')].map((input) => ({
        input,
        error: form.querySelector(`[data-error-for="${input.id}"]`),
        min: Number(input.dataset.minLength || 0)
      }));

      const isValid = ({ input, min }) => {
        const value = input.value.trim();
        if (input.type === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (input.tagName === 'SELECT') return Boolean(value);
        return value.length >= min;
      };

      const setFieldState = (field) => {
        const valid = isValid(field);
        field.input.setAttribute('aria-invalid', String(!valid));
        if (field.error) field.error.classList.toggle('is-visible', !valid);
        return valid;
      };

      const validateAttachment = () => {
        if (!attachment) return true;
        const file = attachment.files?.[0];
        const validTypes = ['image/jpeg', 'image/png'];
        const valid = !file || (validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024);
        attachment.setAttribute('aria-invalid', String(!valid));
        if (attachmentError) attachmentError.classList.toggle('is-visible', !valid);
        return valid;
      };

      fieldConfig.forEach((field) => {
        field.input.addEventListener('blur', () => setFieldState(field));
        field.input.addEventListener('input', () => {
          if (field.input.getAttribute('aria-invalid') === 'true') setFieldState(field);
        });
      });

      if (message && counter) {
        message.addEventListener('input', () => {
          counter.textContent = String(message.value.length);
        });
      }

      attachment?.addEventListener('change', validateAttachment);

      shortcuts.forEach((button) => {
        button.addEventListener('click', () => {
          if (!topic) return;
          topic.value = button.dataset.topicValue;
          shortcuts.forEach((item) => item.classList.toggle('is-selected', item === button));
          const topicField = fieldConfig.find((field) => field.input === topic);
          if (topicField) setFieldState(topicField);
        });
      });

      if (topic) {
        topic.addEventListener('change', () => {
          shortcuts.forEach((button) => {
            button.classList.toggle('is-selected', button.dataset.topicValue === topic.value);
          });
        });
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const cooldownKey = `pfl-contact-sent:${form.action}`;
        const lastSentAt = Number(sessionStorage.getItem(cooldownKey) || 0);
        if (Date.now() - lastSentAt < 30000) {
          if (status) {
            status.hidden = false;
            status.className = 'pfl-contact-form-status error';
            status.textContent = 'Your message was already sent. Please wait a moment before sending again.';
          }
          return;
        }
        const fieldsValid = fieldConfig.map(setFieldState).every(Boolean);
        const attachmentValid = validateAttachment();
        const consentValid = !consent || consent.checked;
        const consentError = form.querySelector('[data-consent-error]');
        if (consentError) consentError.classList.toggle('is-visible', !consentValid);

        if (!fieldsValid || !attachmentValid || !consentValid) {
          const firstInvalid = form.querySelector('[aria-invalid="true"], [data-consent]:not(:checked)');
          firstInvalid?.focus();
          if (status) {
            status.hidden = false;
            status.className = 'pfl-contact-form-status error';
            status.textContent = form.dataset.validationMessage;
          }
          return;
        }

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.classList.add('is-loading');
          const label = submitButton.querySelector('[data-submit-label]');
          if (label) label.textContent = form.dataset.sendingLabel;
        }

        if (status) {
          status.hidden = false;
          status.className = 'pfl-contact-form-status';
          status.textContent = form.dataset.sendingMessage;
        }

        try {
          const response = await fetch(form.action, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const payload = await response.json().catch(() => null);
          const accepted = payload && (payload.success === true || payload.success === 'true');
          const providerMessage = String(payload?.message || '');
          if (!response.ok || !accepted || /activat|confirm/i.test(providerMessage)) {
            throw new Error('Form handler did not confirm delivery.');
          }

          sessionStorage.setItem(cooldownKey, String(Date.now()));
          form.reset();
          if (counter) counter.textContent = '0';
          shortcuts.forEach((button) => button.classList.remove('is-selected'));
          fieldConfig.forEach(({ input }) => input.removeAttribute('aria-invalid'));
          attachment?.removeAttribute('aria-invalid');
          attachmentError?.classList.remove('is-visible');
          if (status) {
            status.className = 'pfl-contact-form-status success';
            status.textContent = form.dataset.successMessage;
          }
        } catch {
          if (status) {
            status.className = 'pfl-contact-form-status error';
            status.textContent = form.dataset.errorMessage;
          }
        } finally {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading');
            const label = submitButton.querySelector('[data-submit-label]');
            if (label) label.textContent = form.dataset.submitLabel;
          }
        }
      });
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-pfl-contact-root]').forEach(initContactSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
